import { PHeadline, PButton, PFlex, PText, PIcon } from '@porsche-design-system/components-react'
import React from 'react'
import { Link } from 'react-router-dom'
import useClippy from 'use-clippy'

import * as AppApi from '../../../utils/api/Apps'
import * as ModuleApi from '../../../utils/api/Modules'
import * as SubAppApi from '../../../utils/api/SubApps'

import { ApplicationContext } from '../../../utils/Context/ApplicationContext'
import Button from './Button'

import {
  API_URL,
  RATING_URL,
  TOAST_TIME
} from '../../../config/config'
import { TEXTS } from '../../../config/texts'
import { PortalContext } from '../../../utils/PortalContext'

import CopyIcon from '../../../assets/copy.svg'
import PAGRating from '../../../Types'
import styles from './drawerdetailsapp.module.css'

const DrawerDetailsApp = () => {
  const [clipboard, setClipboard] = useClippy()

  const { setToast, setToastContent, drawerData, setDrawerContent, loading, setLoading, setOpenDrawer, setError } =
    React.useContext(PortalContext)

  const { applications, setApplications } = React.useContext(ApplicationContext)
  const [routeToReporting, setRouteToReporting] = React.useState('')

  const getRatingUrl = () => RATING_URL
  const getApiUrl = () => API_URL

  const addSubAppOrModule = (event: React.MouseEvent) => {
    event.stopPropagation()
    setDrawerContent('NewSubApp')
  }

  const editApp = (event: React.MouseEvent) => {
    event.stopPropagation()
    setDrawerContent('EditApp')
  }

  const endArchiveProcess = () => {
    setApplications(applications)
    setToastContent(TEXTS.DETAILS_APP_ARCHIVE_TOAST)
    setToast(true)
    setOpenDrawer(false)
    setLoading(false)
    setTimeout(() => {
      setToast(false)
    }, TOAST_TIME)
  }

  const deleteOneApp = () => {
    AppApi.archiveOne(drawerData)
      .then((response) => response)
      .then(() => {
        const index = applications.findIndex((app) => app.app === drawerData.app)
        applications.splice(index, 1)
        endArchiveProcess()
      })
      .catch((err) => {
        setError(true)
        setToastContent(err.message)
        setToast(true)
      })
  }

  const openReporting = () => {
    setOpenDrawer(false)
    setLoading(false)
  }

  const deleteOneSubApp = () => {
    SubAppApi.archiveOne(drawerData)
      .then((response) => response.json())
      .then(() => {
        const appIndex = applications.findIndex(
          (app: { app: number }) => app.app === drawerData.appInformation.parents.idApp
        )
        const index = applications[appIndex].subApp.findIndex(
          (subApp: { subAppId: number }) => subApp.subAppId === drawerData.appInformation.id
        )
        applications[appIndex].subApp.splice(index, 1)
        endArchiveProcess()
      })
      .catch((err) => {
        setError(true)
        setToastContent(err.message)
        setToast(true)
      })
  }

  const deleteOneModule = () => {
    ModuleApi.archiveOne(drawerData)
      .then((response) => response.json())
      .then(() => {
        const appIndex = applications.findIndex(
          (app: { app: number }) => app.app === drawerData.appInformation.parents.idApp
        )
        const subAppindex = applications[appIndex].subApp.findIndex(
          (subApp: { subAppId: number }) => subApp.subAppId === drawerData.appInformation.parents.idSubApp
        )
        const index = applications[appIndex].subApp[subAppindex].modules.findIndex(
          (module: { moduleId: number }) => module.moduleId === drawerData.appInformation.id
        )
        applications[appIndex].subApp[subAppindex].modules.splice(index, 1)
        endArchiveProcess()
      })
      .catch((err) => {
        setError(true)
        setToastContent(err.message)
        setToast(true)
      })
  }

  const generateApiKey = (event: React.MouseEvent) => {
    event.stopPropagation()
    setLoading(true)
    const appId = drawerData.appInformation.type === 'app'
      ? drawerData.app
      : drawerData.appInformation.parents?.idApp
    AppApi.generateApiKey(appId)
      .then((response) => response.json())
      .then((result) => {
        drawerData.appInformation.apiKey = result.apiKey
        setToastContent('API Key wurde generiert!')
        setToast(true)
        setLoading(false)
        setTimeout(() => setToast(false), TOAST_TIME)
      })
      .catch((err) => {
        setError(true)
        setToastContent(err.message)
        setToast(true)
        setLoading(false)
      })
  }

  const archiveApp = (event: React.MouseEvent) => {
    event.stopPropagation()
    setLoading(true)
    if (drawerData.appInformation.type === 'app') {
      return deleteOneApp()
    }
    if (drawerData.appInformation.type === 'subApp') {
      return deleteOneSubApp()
    }
    return deleteOneModule()
  }

  const copyInformationToClipboard = (appInformation: PAGRating.AppInformation, copy: string) => {
    return function (event: React.MouseEvent) {
      event.stopPropagation()
      if (copy === 'api') {
        const api = getApiUrl() + 'configId=' + appInformation.configId
        setClipboard(api)
        setToastContent(TEXTS.DETAILS_APP_API_CLIPBOARD_TOAST)
      }
      if (copy === 'apiRequest') {
        const apiKey = (appInformation as any).apiKey || ''
        const request = `curl -X POST "${getApiUrl()}configId=${appInformation.configId}" -H "Content-Type: application/json" -H "x-api-key: ${apiKey}" -d '{"rating": 5, "comment": ""}'`
        setClipboard(request)
        setToastContent('Rate Request wurde in die Zwischenablage kopiert!')
      }
      if (copy === 'configId') {
        setClipboard(appInformation.configId)
        setToastContent('Applikation Key wurde in die Zwischenablage kopiert!')
      }
      if (copy === 'apiKey') {
        setClipboard((appInformation as any).apiKey || '')
        setToastContent('API Key wurde in die Zwischenablage kopiert!')
      }
      if (copy === 'tab') {
        const apiKeyParam = (appInformation as any).apiKey ? '&apiKey=' + (appInformation as any).apiKey : ''
        const url = getRatingUrl() + '?configId=' + appInformation.configId + apiKeyParam + '&appName=' + appInformation.title
        setClipboard(url)
        setToastContent('URL wurde in die Zwischenablage kopiert!')
      }
      if (copy === 'frame') {
        const apiKeyParam = (appInformation as any).apiKey ? '&apiKey=' + (appInformation as any).apiKey : ''
        const url =
          getRatingUrl() + '?configId=' + appInformation.configId + apiKeyParam + '&frame=true' + '&appName=' + appInformation.title
        setClipboard(url)
        setToastContent('URL für Iframe wurde in die Zwischenablage kopiert!')
      }
      setToast(true)
      setTimeout(() => {
        setToast(false)
      }, TOAST_TIME)
    }
  }

  const countMembers = (members: Array<object>) => {
    return '' + (members.length - 1) + ' weitere Personen'
  }

  React.useEffect(() => {
    if (drawerData.appInformation.type === 'app') {
      setRouteToReporting('/?application=' + drawerData.appInformation.id)
    }
    if (drawerData.appInformation.type === 'subApp') {
      setRouteToReporting(
        '/?application=' + drawerData.appInformation.parents.idApp + '&subapp=' + drawerData.appInformation.id
      )
    }
    if (drawerData.appInformation.type === 'module') {
      setRouteToReporting(
        '/?application=' +
          drawerData.appInformation.parents.idApp +
          '&subapp=' +
          drawerData.appInformation.parents.idSubApp +
          '&module=' +
          drawerData.appInformation.id
      )
    }
  }, [drawerData])

  return (
    <main className={styles.content}>
      {drawerData.appInformation.apiKey && (
        <PFlex alignItems='center' style={{ marginBottom: '12px', gap: '4px' }}>
          <PIcon name='lock' size='small' color='notification-success' />
          <PText size='x-small' color='notification-success'>Rating-Zugriff ist mit API Key geschützt</PText>
        </PFlex>
      )}
      {!drawerData.appInformation.apiKey && drawerData.appInformation.type === 'app' && (
        <PFlex alignItems='center' style={{ marginBottom: '12px', gap: '4px' }}>
          <PIcon name='lock-open' size='small' color='notification-warning' />
          <PText size='x-small' color='notification-warning'>Rating-Zugriff ist nicht geschützt</PText>
        </PFlex>
      )}
      <PFlex justifyContent='space-between' className={styles.info}>
        <PText>PlanningIT ICTO</PText>
        <PText align='right'>{drawerData.appInformation.icto}</PText>
      </PFlex>
      <PFlex justifyContent='space-between' className={styles.info}>
        <PText>PlanningIT App-ID</PText>
        <PText align='right'>{drawerData.appInformation.appId === '' ? '-' : drawerData.appInformation.appId}</PText>
      </PFlex>
      <PFlex justifyContent='space-between' className={styles.info}>
        <PText>Org-Einheit</PText>
        <PText align='right'>{drawerData.appInformation.department}</PText>
      </PFlex>
      <PFlex justifyContent='space-between' className={styles.info}>
        <PText>Application Manager</PText>
        <PText align='right'>{drawerData.appInformation.members[0].name}</PText>
      </PFlex>
      <PFlex justifyContent='space-between' className={styles.info}>
        <PText>E-Mail</PText>
        <PText align='right' ellipsis className={styles.mail}>
          {drawerData.appInformation.members[0].email ? drawerData.appInformation.members[0].email : '-'}
        </PText>
      </PFlex>
      <PFlex justifyContent='space-between' className={styles.info}>
        <PText>Zugriffsberechtigt</PText>
        <PText align='right' ellipsis className={styles.membersTile}>
          {drawerData.appInformation.members.length > 1 ? countMembers(drawerData.appInformation.members) : '-'}
        </PText>
        {drawerData.appInformation.members.length > 1 && (
          <div className={styles.members}>
            <PText>Zugriffsberechtigt</PText>
            {drawerData.appInformation.members.map((val: { email: string }, ind: number) => {
              if (ind === 0) {
                return
              }
              return (
                <PText key={ind} ellipsis>
                  {val.email}
                </PText>
              )
            })}
          </div>
        )}
      </PFlex>
      <PButton variant='tertiary' icon='edit' onClick={editApp}>
        Bearbeiten
      </PButton>
      <PHeadline variant='headline-5' className={styles.subtitle}>
        Integration
      </PHeadline>
      <PText color='neutral-contrast-medium' size='small'>
        URL
      </PText>
      <Button
        icon={CopyIcon}
        size='small'
        onClick={copyInformationToClipboard(drawerData.appInformation, 'tab')}
        buttonText='URL Neuer Tab kopieren'
      />
      <Button
        icon={CopyIcon}
        size='small'
        onClick={copyInformationToClipboard(drawerData.appInformation, 'frame')}
        buttonText='URL Pop-up kopieren'
      />

      <PText color='neutral-contrast-medium' size='small'>
        API
      </PText>

      <Button
        icon={CopyIcon}
        size='small'
        onClick={copyInformationToClipboard(drawerData.appInformation, 'configId')}
        buttonText='Config-ID kopieren'
      />

      {(drawerData.appInformation as any).apiKey && (
        <Button
          icon={CopyIcon}
          size='small'
          onClick={copyInformationToClipboard(drawerData.appInformation, 'apiKey')}
          buttonText='API Key kopieren'
        />
      )}

      {(drawerData.appInformation as any).apiKey && (
        <Button
          icon={CopyIcon}
          size='small'
          onClick={copyInformationToClipboard(drawerData.appInformation, 'apiRequest')}
          buttonText='Rate Request kopieren (cURL)'
        />
      )}

      <PHeadline variant='headline-5' className={styles.subtitle}>
        Aktionen
      </PHeadline>
      {drawerData.appInformation.type !== 'module' && (
        <Button icon='plus' size='small' onClick={addSubAppOrModule} buttonText='Unterbereich hinzufügen' />
      )}
      {drawerData.appInformation.type === 'app' && (
        <Button icon='refresh' size='small' onClick={generateApiKey} loading={loading} buttonText='API Key generieren' />
      )}
      <Button icon='save' size='small' onClick={archiveApp} loading={loading} buttonText='Bereich(e) archivieren' />
      <Link to={routeToReporting} className={styles.hiddenLink}>
        <Button icon='chart' size='small' onClick={openReporting} buttonText='Bewertungen anzeigen' />
      </Link>
    </main>
  )
}

export default DrawerDetailsApp
